import { useQuery, useQueryClient } from '@tanstack/react-query';
import { QUERY_CONFIG } from '@/config/constants';
import { QUERY_KEYS } from '@/config/queries';
import { useAuth } from '@/hooks/auth/use-auth';
import { useMemo, useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { fetchDashboardData, type HealthMetrics } from '@/lib/data/dashboard';
import { getLocalIsoDate } from '@/lib/dateUtils';
import { NATIVE_SMOKE_ACTIVE_KEY, NATIVE_SMOKE_ROUTE_KEY } from '@/lib/native-app-routes';
import { DEFAULT_DASHBOARD_METRICS } from '@/types/dashboard';

const MINUTE_MS = 60_000;
const NEXT_DAY_BUFFER_MS = 5_000;

export const getMsUntilNextLocalDashboardDate = (now = new Date()): number => {
  const nextDay = new Date(now);
  nextDay.setHours(24, 0, 0, NEXT_DAY_BUFFER_MS);
  return Math.max(MINUTE_MS, nextDay.getTime() - now.getTime());
};

/**
 * Custom hook to fetch dashboard data for a user
 *
 * This is a UI wrapper around the pure data layer in lib/data/dashboard.ts
 * The hook uses React Query for caching and state management, while the
 * actual data fetching is done by pure functions that can be reused
 * in edge functions or other non-React contexts.
 *
 * @param userId - User ID to fetch data for
 * @returns Object containing profile, metrics, points, streak, and loading state
 *
 * @example
 * // Normal usage
 * const { profile, metrics, points, streak, isLoading } = useDashboardData('user-123');
 *
 * @example
 * // Edge case: no user ID (will not fetch)
 * const data = useDashboardData(undefined);
 * // => { profile: null, metrics: {...defaults}, points: 0, streak: 0, isLoading: false }
 */
export const useDashboardData = (userId: string | undefined) => {
  const { session } = useAuth();
  const queryClient = useQueryClient();
  const [today, setToday] = useState(() => getLocalIsoDate());
  const nativeSmokeRoutesEnabled =
    import.meta.env.DEV || import.meta.env.VITE_ENABLE_NATIVE_SMOKE === 'true';
  const useNativeSmokeFallback =
    nativeSmokeRoutesEnabled &&
    Capacitor.isNativePlatform() &&
    typeof window !== 'undefined' &&
    (window.localStorage.getItem(NATIVE_SMOKE_ROUTE_KEY) !== null ||
      window.localStorage.getItem(NATIVE_SMOKE_ACTIVE_KEY) === '1');

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const refreshToday = () => {
      const currentDate = getLocalIsoDate();
      setToday((previousDate) => (previousDate === currentDate ? previousDate : currentDate));
    };

    const scheduleNextDayRefresh = () => {
      timeoutId = setTimeout(() => {
        refreshToday();
        scheduleNextDayRefresh();
      }, getMsUntilNextLocalDashboardDate());
    };

    scheduleNextDayRefresh();
    window.addEventListener('focus', refreshToday);
    document.addEventListener('visibilitychange', refreshToday);

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      window.removeEventListener('focus', refreshToday);
      document.removeEventListener('visibilitychange', refreshToday);
    };
  }, []);

  // The data layer runs the profile, metrics, points, and streak reads in parallel
  // and exposes them as one hook-level payload.
  const {
    data: dashboardData,
    isLoading,
    isError,
    error,
    dataUpdatedAt,
    refetch,
  } = useQuery({
    queryKey: QUERY_KEYS.DASHBOARD(userId ?? '', today),
    queryFn: () => fetchDashboardData(userId!, session!, today),
    enabled: !!userId && !!session && !useNativeSmokeFallback,
    initialData:
      userId && session?.user
        ? {
            profile: {
              id: session.user.id,
              email: session.user.email ?? '',
              avatar_url: null,
              consent_data_processing: false,
            },
            metrics: DEFAULT_DASHBOARD_METRICS,
            points: 0,
            streak: 0,
          }
        : undefined,
    initialDataUpdatedAt: 0,
    staleTime: QUERY_CONFIG.STALE_TIME.LONG, // 5 minutes
    gcTime: QUERY_CONFIG.GC_TIME.MEDIUM, // 10 minutes cache
    refetchOnMount: true,
    refetchOnReconnect: true,
    refetchOnWindowFocus: false,
    retry: false,
    // Structural sharing prevents unnecessary re-renders
    structuralSharing: true,
    // Show cached data immediately while refetching in background
    placeholderData: (previousData) => previousData,
  });

  // Populate individual query keys in cache for granular invalidation
  // Note: onSuccess is deprecated in React Query v5, using useEffect instead
  // IMPORTANT: We do NOT setQueryData for PROFILE because dashboard only fetches
  // partial profile data (id, email) which would overwrite the full profile data
  // (including consent fields) that Profile.tsx fetches. This was causing consent
  // checkboxes to appear unchecked when navigating from Dashboard to Profile.
  useEffect(() => {
    if (dashboardData && userId) {
      queryClient.setQueryData(QUERY_KEYS.METRICS(userId, today), dashboardData.metrics);
      queryClient.setQueryData(QUERY_KEYS.POINTS(userId), dashboardData.points);
      queryClient.setQueryData(QUERY_KEYS.STREAK(userId), dashboardData.streak);
    }
  }, [dashboardData, userId, today, queryClient]);

  // Extract data from batched response
  const profile =
    dashboardData?.profile ??
    (userId && session?.user
      ? {
          id: session.user.id,
          email: session.user.email ?? '',
          avatar_url: null,
          consent_data_processing: false,
        }
      : null);

  const metrics = useMemo(
    () =>
      dashboardData?.metrics ?? {
        steps: 0,
        sleep_hours: 0,
        sleep_efficiency: 0,
        sleep_deep_minutes: 0,
        sleep_rem_minutes: 0,
        sleep_start_time: null,
        active_minutes: 0,
        heart_rate: 0,
        hrv_rmssd: 0,
        hrv_coverage: 0,
        calories: 0,
        distance: 0,
        floors: 0,
        weight: 0,
        body_fat: 0,
        oxygen_saturation: 0,
        respiratory_rate: 0,
        temperature: 0,
        cardio_fitness: 0,
        metric_source_date: null,
      },
    [dashboardData?.metrics],
  );

  const points = dashboardData?.points ?? 0;
  const streak = dashboardData?.streak ?? 0;

  // Memoize the returned metrics object to prevent unnecessary re-renders
  const memoizedMetrics = useMemo<HealthMetrics>(
    () => ({
      steps: metrics.steps || 0,
      sleep_hours: metrics.sleep_hours || 0,
      sleep_efficiency: metrics.sleep_efficiency || 0,
      sleep_deep_minutes: metrics.sleep_deep_minutes || 0,
      sleep_rem_minutes: metrics.sleep_rem_minutes || 0,
      sleep_start_time: metrics.sleep_start_time ?? null,
      active_minutes: metrics.active_minutes || 0,
      heart_rate: metrics.heart_rate || 0,
      hrv_rmssd: metrics.hrv_rmssd || 0,
      hrv_coverage: metrics.hrv_coverage || 0,
      calories: metrics.calories || 0,
      distance: metrics.distance || 0,
      floors: metrics.floors || 0,
      weight: metrics.weight || 0,
      body_fat: metrics.body_fat || 0,
      oxygen_saturation: metrics.oxygen_saturation || 0,
      respiratory_rate: metrics.respiratory_rate || 0,
      temperature: metrics.temperature || 0,
      cardio_fitness: metrics.cardio_fitness || 0,
      metric_source_date: metrics.metric_source_date ?? null,
    }),
    [metrics],
  );

  // `initialDataUpdatedAt: 0` marks the session-derived stub above, so a zero
  // timestamp means nothing has been read from the backend yet and the metric
  // values are placeholders rather than health readings.
  const hasVerifiedData = useNativeSmokeFallback ? true : dataUpdatedAt > 0;

  return {
    profile,
    metrics: memoizedMetrics,
    points,
    streak,
    isLoading: useNativeSmokeFallback ? false : isLoading,
    isError: useNativeSmokeFallback ? false : isError,
    error: useNativeSmokeFallback ? null : (error ?? null),
    hasVerifiedData,
    refetch,
  };
};

// Re-export for convenience
export { fetchDashboardData };
export { fetchProfile, fetchMetrics, fetchPoints, fetchStreak } from '@/lib/data/dashboard';
export type { DashboardData, HealthMetrics, Profile } from '@/lib/data/dashboard';
